import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Button, Text } from 'bench-internal-theme';
import { TextInputHookForm } from 'bench-internal-theme';
import { Modal } from 'modal-components';
import { useForm, FormProvider } from 'react-hook-form';
import { Moment } from 'moment/moment';
import { colors } from 'bench-style-constants';
import moment from 'moment';

const ModalBodyLayout = styled.div`
  display: grid;
  gap: 16px;
`;

const ButtonLayout = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;
`;

const InputLayout = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr;

  .input {
    // prevents the input from stretching when one input has a validation error and the other does not
    max-height: 32px;
  }
`;

type DateRangePickerModalProps = {
  closeModal: () => void;
  description?: string;
  onConfirm: (startDate: string, endDate: string) => void;
  title: string;
  defaultStartDate?: Moment | undefined | null;
  defaultEndDate?: Moment | undefined | null;
  helperText?: string | undefined | null;
  minValidDate?: Moment | undefined | null;
};

const DateRangePickerModal = ({
  defaultStartDate,
  defaultEndDate,
  closeModal,
  onConfirm,
  title,
  description,
  helperText,
  minValidDate,
}: DateRangePickerModalProps) => {
  const methods = useForm();
  const { handleSubmit, watch, setValue } = methods;
  const startDate = watch('startDate', undefined);
  const endDate = watch('endDate', undefined);

  useEffect(() => {
    if (defaultStartDate) {
      setValue('startDate', defaultStartDate.format('YYYY-MM-DD'));
    }
    if (defaultEndDate) {
      setValue('endDate', defaultEndDate.format('YYYY-MM-DD'));
    }
  }, [defaultStartDate, defaultEndDate, setValue]);

  const validateStartDate = (value: string | undefined) => {
    if (!minValidDate) return true;
    if (!value || moment(value).isSameOrAfter(minValidDate)) return true;
    return `Start date cannot be before ${minValidDate.format('YYYY-MM-DD')}`;
  };

  return (
    <Modal>
      <Modal.Header title={title} onClose={closeModal} />
      <Modal.Body>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(fieldValues =>
              onConfirm(fieldValues?.startDate, fieldValues?.endDate),
            )}
          >
            <ModalBodyLayout>
              {description && (
                <Text styleType="small" color={colors.cloud}>
                  {description}
                </Text>
              )}
              <InputLayout>
                <div>
                  <TextInputHookForm
                    aria-label="Start Date"
                    name="startDate"
                    rules={{
                      required: {
                        value: true,
                        message: 'Start date is required',
                      },
                      validate: {
                        isBeforeEndDate: (value: string | undefined) =>
                          (endDate && value && moment(value).isBefore(moment(endDate))) ||
                          moment(value).isSame(endDate) ||
                          !endDate ||
                          'Cannot be after end date',
                        isAfterMinValidDate: validateStartDate,
                      },
                    }}
                    type="date"
                  />
                </div>
                <div>
                  <TextInputHookForm
                    aria-label="End Date"
                    name="endDate"
                    rules={{
                      required: { value: true, message: 'End date is required' },
                      validate: {
                        isAfterStartDate: (value: string | undefined) =>
                          (startDate &&
                            value &&
                            (moment(value).isAfter(moment(startDate)) ||
                              moment(value).isSame(moment(startDate)))) ||
                          'Cannot be before start date',
                        isBeforeToday: (value: string | undefined) =>
                          (value &&
                            (moment(value).isBefore(moment()) || moment(value).isSame(moment()))) ||
                          'Cannot be before today',
                      },
                    }}
                    type="date"
                  />
                </div>
              </InputLayout>
              {helperText && (
                <Text styleType="small" color={colors.cloud}>
                  {helperText}
                </Text>
              )}
              <ButtonLayout>
                <Button onClick={closeModal} styleType="ghost">
                  Cancel
                </Button>
                <Button role="button" type="submit" aria-label="Confirm" styleType="primary">
                  Confirm
                </Button>
              </ButtonLayout>
            </ModalBodyLayout>
          </form>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};

DateRangePickerModal.defaultProps = {
  description: undefined,
  defaultStartDate: undefined,
  defaultEndDate: undefined,
  helperText: undefined,
  minValidDate: undefined,
};

export default DateRangePickerModal;
